/**
 * Import history — prototype `histModal` / `hiType` / `hiRead` / `hiApply` (docs/prototype.html 2920-2945; ADR-001
 * §10.1). Managers bring in old tasks, or old escalations and mess-ups, from a sheet so the record starts from the past.
 *
 * Opened from Work's "+ Add" chooser ("Old tasks or old escalations") and from the Work toolbar's "Import old tasks" /
 * "Import old escalations" button. Both are offered only to callers with task.reassign, the capability every
 * /tasks/history-import route requires (contracts routes.history.ts MANAGERS), so a member never sees an entry the
 * server would refuse. `can` drops write capabilities while viewing as someone, so view-as hides it too.
 *
 * Picking a file: sign → PUT → dry run → poll the per-row preview (lib/history.ts). The preview is the prototype's:
 * a scrolling table (Task | What happened, Client, SPOC | Owner, Dates, a Ready or why-not chip), then "N of M rows
 * ready", Cancel and "Import N". States: reading; no rows ("No rows found."); the worker could not read the sheet
 * (its parse_error, and the field is cleared for a fresh upload); a refusal (the server's own words, with "Read it
 * again" when the sheet is already up). Importing writes the ready rows and refreshes every work list.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { HistoryImportDetail, HistoryImportKind, HistoryImportRow, IncidentCause, IncidentKind } from '@amplispot/contracts';
import { firstName } from '../../lib/format';
import { applyHistory, historyFailText, previewHistory, sendHistorySheet } from '../../lib/history';
import { shortDay } from '../../lib/requests';
import { refreshAfterWrite } from '../../lib/work';
import { Loading, Modal, Seg, useToast } from '../../ui/kit';

/** Prototype `tpl` strings, with placeholder people: sample people may only be named in db/seed (ADR-001 §1.4, check:no-hardwired-people). */
const TEMPLATE: Record<HistoryImportKind, { file: string; csv: string }> = {
  tasks: {
    file: 'old-tasks-template.csv',
    csv:
      'Task,Client,SPOC,Created on,Due on,Closed on,Status\n' +
      'Send the September MIS,Meridian Insurance,Asha,2 Sep,10 Sep,12 Sep,Closed\n' +
      'Fix template rejection,Meridian Insurance,Asha,5 Sep,8 Sep,,Open\n',
  },
  incidents: {
    file: 'old-escalations-template.csv',
    csv:
      'What happened,Type,Client,Owner,Raised on,Closed on,Severity,Caused by,What went wrong,Outcome\n' +
      'Wrong MIS sent to Kestrel,Escalation,Kestrel Finance,Ravi,12 Aug,12 Aug,High,Ravi,Attached the wrong file,Corrected in 2 hours\n' +
      'Template rejected three times,Escalation,Meridian Insurance,Asha,3 Sep,10 Sep,High,Vendor,Platform rejected templates,Approved on 4th try\n',
  },
};

export const HISTORY_ACCEPT = '.xlsx,.csv,.tsv,.txt';

/** Prototype IKIND. */
const KIND_LABEL: Record<IncidentKind, string> = {
  escalation: 'Escalation',
  missed: 'Missed deadline',
  late: 'Late response',
  slip: 'Date slipped',
  complaint: 'Client complaint',
  closure: 'Bad closure',
  mistake: 'Mistake',
};
/** Prototype CAUSES labels. */
const CAUSE_LABEL: Record<IncidentCause, string> = {
  person: 'Our mistake',
  process: 'Process gap',
  vendor: 'Vendor or platform',
  client: 'Client side',
  none: 'Unavoidable',
  unclear: 'Not reviewed yet',
};

function Columns({ kind }: { kind: HistoryImportKind }) {
  return kind === 'tasks' ? (
    <>
      Columns: <b>Task, Client, SPOC, Created on, Due on, Closed on, Status</b>. A task with a Closed on date, or a status of Closed or Done, is recorded as
      finished.
    </>
  ) : (
    <>
      Columns: <b>What happened, Type</b> (escalation, mistake, missed deadline, late response, complaint), <b>Client, Owner</b> (on whose watch),{' '}
      <b>Raised on, Closed on, Severity, Caused by</b> (a person, vendor, client, process or none), <b>What went wrong, Outcome</b>.
    </>
  );
}

type Step =
  | { k: 'idle' }
  | { k: 'reading'; name: string }
  /** `id`: the sheet is up, so reading it again is one more dry run. null: start again with a fresh upload. */
  | { k: 'failed'; msg: string; id: string | null }
  | { k: 'preview'; d: HistoryImportDetail };

export function HistoryImportModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  // Prototype: histModal always opens on Old tasks (HI={type:'tasks'}), whichever entry opened it.
  const [kind, setKind] = useState<HistoryImportKind>('tasks');
  const [step, setStep] = useState<Step>({ k: 'idle' });
  const [applying, setApplying] = useState(false);
  const [applyErr, setApplyErr] = useState<string | null>(null);
  // Bumped to empty the file field (a kind switch, a sheet that could not be read), so even the same file can be
  // picked again for a fresh upload.
  const [field, setField] = useState(0);
  // Which pick is current. A re-pick, a kind switch or closing moves it on, and an older read's answer is dropped.
  const run = useRef(0);
  useEffect(
    () => () => {
      run.current++;
    },
    [],
  );

  const read = async (id: string, mine: number) => {
    const alive = () => mine === run.current;
    try {
      const d = await previewHistory(id, alive);
      if (!d || !alive()) return;
      if (d.status === 'failed') {
        setStep({ k: 'failed', msg: d.parse_error ?? 'This file could not be read. Save it as .xlsx or .csv and try again.', id: null });
        setField((n) => n + 1);
      } else setStep({ k: 'preview', d });
    } catch (e) {
      if (alive()) setStep({ k: 'failed', msg: historyFailText(e), id });
    }
  };

  const pick = async (f: File | undefined) => {
    const mine = ++run.current;
    setApplyErr(null);
    if (!f) return setStep({ k: 'idle' });
    setStep({ k: 'reading', name: f.name });
    let id: string;
    try {
      id = await sendHistorySheet(kind, f);
    } catch (e) {
      if (mine === run.current) setStep({ k: 'failed', msg: historyFailText(e), id: null });
      return;
    }
    if (mine === run.current) await read(id, mine);
  };

  const again = (id: string) => {
    const mine = ++run.current;
    setStep({ k: 'reading', name: 'the file' });
    void read(id, mine);
  };

  const switchKind = (k: HistoryImportKind) => {
    run.current++;
    setKind(k);
    setStep({ k: 'idle' });
    setApplyErr(null);
    setField((n) => n + 1);
  };

  const apply = async (d: HistoryImportDetail) => {
    setApplying(true);
    setApplyErr(null);
    try {
      const r = await applyHistory(d.id);
      refreshAfterWrite(qc);
      const n = d.kind === 'tasks' ? r.written.tasks : r.written.incidents;
      // Prototype hiApply's toast.
      toast(
        d.kind === 'tasks'
          ? `${n} old task${n === 1 ? '' : 's'} imported.`
          : `${n} old record${n === 1 ? '' : 's'} added to the appraisal record.`,
      );
      onClose();
    } catch (e) {
      setApplyErr(historyFailText(e));
      setApplying(false);
    }
  };

  const t = TEMPLATE[kind];
  return (
    <Modal
      wide
      title="Import history"
      sub="Bring in old tasks and old escalations so the record starts from the past. Old escalations and mess-ups land in each person’s appraisal record."
      onClose={onClose}
      // As the prototype: no footer band; the preview brings its own Cancel and "Import N".
      footer={null}
    >
      <div style={{ marginBottom: 12 }}>
        <Seg<HistoryImportKind>
          options={[
            ['tasks', 'Old tasks'],
            ['incidents', 'Old escalations and mess-ups'],
          ]}
          value={kind}
          onChange={switchKind}
        />
      </div>
      <div className="note" style={{ marginBottom: 12 }}>
        <Columns kind={kind} /> Dates can be 12 Sep, 12/09/2026 or Excel dates.{' '}
        <a href={`data:text/csv;charset=utf-8,${encodeURIComponent(t.csv)}`} download={t.file} style={{ color: 'var(--brand)', fontWeight: 600 }}>
          Download a template
        </a>
      </div>
      <label className="fld">
        <span>Excel or CSV file</span>
        <input
          key={`${kind}-${field}`}
          type="file"
          accept={HISTORY_ACCEPT}
          className="in"
          disabled={applying}
          onChange={(e) => void pick(e.target.files?.[0])}
        />
      </label>
      {step.k === 'reading' ? <Loading label={`Reading ${step.name}…`} /> : null}
      {step.k === 'failed' ? (
        <div className="note warn" role="alert" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="grow" style={{ minWidth: 0 }}>
            {step.msg}
            {step.id ? '' : ' Choose the file again, or another one.'}
          </span>
          {step.id ? (
            <button className="btn sm" onClick={() => again(step.id!)}>
              Read it again
            </button>
          ) : null}
        </div>
      ) : null}
      {step.k === 'preview' ? <Preview d={step.d} applying={applying} applyErr={applyErr} onCancel={onClose} onImport={() => void apply(step.d)} /> : null}
    </Modal>
  );
}

const rowTitle = (r: HistoryImportRow) => r.task?.title || r.incident?.title || '';

function rowDates(r: HistoryImportRow): string {
  const [from, to] = r.task ? [r.task.due_on, r.task.closed_on] : r.incident ? [r.incident.raised_on, r.incident.closed_on] : [null, null];
  return `${from ? shortDay(from) : ''}${to ? ` to ${shortDay(to)}` : ''}`;
}

function Preview(props: { d: HistoryImportDetail; applying: boolean; applyErr: string | null; onCancel: () => void; onImport: () => void }) {
  const { d } = props;
  if (d.rows.length === 0) return <div className="note warn">No rows found.</div>;
  const tasks = d.kind === 'tasks';
  const ready = d.rows.filter((r) => r.status === 'ready').length;
  return (
    <>
      <div className="panel" style={{ margin: '8px 0 12px', maxHeight: 300, overflow: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>{tasks ? 'Task' : 'What happened'}</th>
              <th>Client</th>
              <th>{tasks ? 'SPOC' : 'Owner'}</th>
              <th>Dates</th>
              <th aria-label="Ready or why not" />
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => {
              const who = r.task?.assignee ?? r.incident?.owner ?? null;
              const client = r.task?.client ?? r.incident?.client ?? null;
              return (
                <tr key={r.row_number}>
                  <td>
                    <b>{rowTitle(r) || '(missing)'}</b>
                    {r.incident ? (
                      <div className="sub">
                        {KIND_LABEL[r.incident.kind]}
                        {r.incident.cause ? `, ${CAUSE_LABEL[r.incident.cause]}` : ''}
                      </div>
                    ) : null}
                  </td>
                  <td>{client ? client.name : '-'}</td>
                  <td>{who ? firstName(who.name) : '-'}</td>
                  <td className="sub">{rowDates(r)}</td>
                  <td>
                    {r.error ? (
                      <span className="chip warm" title={`Row ${r.row_number}`}>
                        {r.error.detail}
                      </span>
                    ) : (
                      <span className="chip hot">Ready</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {props.applyErr ? (
        <div className="note bad" role="alert" style={{ marginBottom: 10 }}>
          {props.applyErr}
        </div>
      ) : null}
      <div className="split">
        <span className="sub">
          {ready} of {d.rows.length} rows ready
        </span>
        <div className="grow" />
        <button className="btn" onClick={props.onCancel}>
          Cancel
        </button>
        <button className="btn pri" onClick={props.onImport} disabled={!ready || props.applying}>
          {props.applying ? 'Importing…' : `Import ${ready}`}
        </button>
      </div>
    </>
  );
}
